import { AUTH_QUERY_KEYS } from '@/constants/query-keys';
import { gtagCommand, pushDataLayer } from '@/lib/analytics';
import {
  captureAttribution,
  clearAttribution,
  siteCookieDomain,
} from '@/lib/attribution';
import { queryClient } from '@/lib/query-client';

export type ConsentChoice = 'granted' | 'denied';

declare global {
  interface Window {
    CORETAS_CONSENT_PROMPT?: boolean;
    CORETAS_CONSENT_SIGNAL?: boolean;
    CORETAS_CONSENT_GRANTED?: boolean;
  }
}

// Shared with coretas.ai's consent.js and the head block in index.html.
export const CONSENT_COOKIE = 'coretas_consent';
const VERSION = 1;
const MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

// The click ids a visitor landed with wait here until they accept.
let landingSearch = '';

export const consentCookieValue = (
  choice: ConsentChoice,
  now: number = Date.now(),
): string =>
  encodeURIComponent(JSON.stringify({ v: VERSION, s: choice, t: now }));

export const consentCookie = (
  choice: ConsentChoice,
  { hostname, protocol }: Pick<Location, 'hostname' | 'protocol'>,
  now: number = Date.now(),
): string => {
  const secure = protocol === 'https:' ? '; Secure' : '';
  return `${CONSENT_COOKIE}=${consentCookieValue(choice, now)}; path=/${siteCookieDomain(hostname)}; max-age=${MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
};

export const recordConsent = (choice: ConsentChoice): void => {
  document.cookie = consentCookie(choice, window.location);
  gtagCommand('consent', 'update', {
    ad_storage: choice,
    ad_user_data: choice,
    ad_personalization: choice,
    analytics_storage: choice,
  });
  pushDataLayer({ event: `coretas_consent_${choice}` });
  window.CORETAS_CONSENT_GRANTED = choice === 'granted';
  // Re-identifies the GA4 user under the choice; a denial also makes the API drop stored GA ids.
  void queryClient.invalidateQueries({ queryKey: AUTH_QUERY_KEYS.me });
  if (choice === 'granted') {
    captureAttribution(landingSearch);
    return;
  }
  clearAttribution();
};

// A browser privacy signal is stored as a denial, as coretas.ai does, so the API reads it too.
export const applyConsentSignal = (): void => {
  if (window.CORETAS_CONSENT_SIGNAL !== true) return;
  window.CORETAS_CONSENT_SIGNAL = false;
  recordConsent('denied');
};

// Ad-click attribution is advertising storage: it is written only under granted consent.
export const startConsent = (): void => {
  landingSearch = window.location.search;
  if (window.CORETAS_CONSENT_SIGNAL === true) {
    applyConsentSignal();
    return;
  }
  // A click stored before this visitor's denial, or by older code, must not reach signup.
  if (window.CORETAS_CONSENT_GRANTED === true)
    captureAttribution(landingSearch);
  else clearAttribution();
};
